import { useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useApolloClient } from '@apollo/client';

import { VisitorPassQueryResponse } from 'lane-shared/domains/visitorManagement/types';
import {
  updateVisitorPassStatus,
  getVisitorPassDetailsById
} from 'lane-shared/graphql/visitorManagement';
import { updateVisitorPasses, getVisitorPassUpdateOperationStatus } from 'graphql-queries';
import { VisitorPassUpdateResult } from 'graphql-query-contracts';

const getVisitorName = (pass: VisitorPassQueryResponse) => {
  let visitorName = `${pass.visitor.firstName}`;

  if (pass.visitor.lastName) {
    visitorName += ` ${pass.visitor.lastName}`;
  }

  return visitorName;
};

export const useStatusSelector = () => {
  const apolloClient = useApolloClient();
  const { t } = useTranslation();

  const [loadingStates, setLoadingStates] = useState<{
    [passId: string]: boolean;
  }>({});

  const updateLoadingState = (passId: string, isLoading: boolean) => {
    setLoadingStates(existingLoadingStates => {
      return {
        ...existingLoadingStates,
        [passId]: isLoading,
      };
    });
  };

  const handleUpdateStatus = async (
    status: string,
    pass: VisitorPassQueryResponse
  ) => {
    try {
      updateLoadingState(pass.id, true);

      // update mutation
      await apolloClient.mutate({
        mutation: updateVisitorPassStatus,
        variables: {
          visitorPassId: pass.id,
          status,
        },
      });

      // query latest changes
      await apolloClient.query({
        query: getVisitorPassDetailsById,
        variables: {
          id: pass.id,
          channelId: pass.accessGrantBuildingId,
        },
        fetchPolicy: 'network-only', // automatically updates the cache using the typename and id in the result
      });

      updateLoadingState(pass.id, false);
      // FIXME: Log error for datadog, missing stack trace
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
    } catch (error) {
      updateLoadingState(pass.id, false);
      const visitorName = getVisitorName(pass);
      const errorMessage = t(
        `error: status update failed for visitor '${visitorName}'`
      );

      throw new Error(errorMessage);
    }
  };

  const pollOperationResult = async (
    operationId: string,
    expectedStatus: string,
    channelId: string,
    maxRetries: number = 20
  ): Promise<VisitorPassUpdateResult[] | null> => {
    let retryCount = 0;

    while (retryCount < maxRetries) {
      try {
        await new Promise<void>(resolve => {
          setTimeout(resolve, 3000);
        });

        const updateOperationResult = await apolloClient.query({
          query: getVisitorPassUpdateOperationStatus,
          variables: {
            operationId,
            channelId,
          },
          fetchPolicy: 'network-only',
        });

        if (updateOperationResult.data.getVisitorPassUpdateOperationStatus.status === expectedStatus) {
          return updateOperationResult.data.getVisitorPassUpdateOperationStatus.results;
        }

        retryCount++;
      } catch (error) {
        console.error('Error polling operation result:', error);
        retryCount++;
      }
    }

    return null;
  };

  const handleBulkUpdateStatus = async (
    channelId: string,
    status: string,
    passes: VisitorPassQueryResponse[]
  ) => {
    try {
      // Set loading state for all passes
      passes.forEach(pass => {
        updateLoadingState(pass.id, true);
      });

      const visitorPassIds = passes.map(pass => pass.id);

      const result = await apolloClient.mutate({
        mutation: updateVisitorPasses,
        variables: {
          visitorPassIds,
          channelId,
          eventStatus: status,
        },
      });

      const operationId = result.data?.updateVisitorPassesStatus?.operationId;

      if (!operationId) {
        throw new Error('Operation ID is missing from the update response');
      }

      // Poll for operation result every 3 seconds until success
      const updateOperationResult = await pollOperationResult(
        operationId,
        'COMPLETED',
        channelId,
        20 // Max 60 seconds (20 retries * 3 seconds)
      );

      if (!updateOperationResult) {
        throw new Error('Polling bulk update operation status timed out waiting for confirmation');
      }

      return updateOperationResult;
    } catch (error) {
      passes.forEach(pass => {
        updateLoadingState(pass.id, false);
      });

      throw error;
    }
  };

  return {
    loadingStates,
    updateStatus: handleUpdateStatus,
    updateBulkStatus: handleBulkUpdateStatus,
    updateLoadingState,
  };
};
